import { useEffect, useState } from "react";
import { requestExternalNavigation } from "../../../../utils/externalNavigation";
import PlaybackButton from "../PlaybackButton";
import useMovingContentAccessibility from "../../../../hooks/useMovingContentAccessibility";
import { Card, CTAButton, StatsTrack } from "./Dashboard.styles";

import graph1 from "../../../../assets/pages/landing/dashboard/grahp1.svg";
import graph2 from "../../../../assets/pages/landing/dashboard/grahp2.svg";
import graph3 from "../../../../assets/pages/landing/dashboard/grahp3.svg";
import graph4 from "../../../../assets/pages/landing/dashboard/grahp4.svg";
import graph5 from "../../../../assets/pages/landing/dashboard/grahp5.svg";
import usersIcon from "../../../../assets/pages/landing/dashboard/people.svg";
import loanIcon from "../../../../assets/pages/landing/dashboard/rupee.svg";
import docIcon from "../../../../assets/pages/landing/dashboard/document.svg";
import femaleIcon from "../../../../assets/pages/landing/dashboard/females.svg";
import performanceIcon from "../../../../assets/pages/landing/dashboard/projection.svg";

const POWER_BI_DASHBOARD_URL = "https://app.powerbi.com/view?r=eyJrIjoiNGIxZGQ5ZjMtNmQ0OS00MGQ0LTgwNWMtZDc0OTI3NjQ1ZmFjIiwidCI6IjM2Y2U2MDMxLTNmMjItNDZmMi1iYWE5LTNiNDNmNDUwN2VkMSJ9&pageName=0f096019775402a7946e";

// 1. Updated stats array to separate numeric values from text suffixes
const stats = [
  {
    value: 7289,
    suffix: " Cr",
    isFloat: false,
    title: "Total Loan",
    subtitle: "Amount Disbursed",
    bg: "bg-[#b31469]",
    icon: loanIcon,
  },
  {
    value: 17,
    suffix: " Lakh",
    isFloat: false,
    title: "Distinct",
    subtitle: " Beneficiaries",
    bg: "bg-[#c04414]",
    icon: usersIcon,
  },
  {
    value: 4,
    suffix: " Lakh",
    isFloat: false,
    title: "Applications ",
    subtitle: "Processed",
    bg: "bg-[#054a78]",
    icon: docIcon,
  },
  {
    value: 8961,
    suffix: "",
    isFloat: false,
    title: "Females per",
    subtitle: " 1000 Males",
    bg: "bg-[#1f7a22]",
    icon: femaleIcon,
  },
  {
    value: 15.75,
    suffix: "%",
    isFloat: true,
    title: "Year on Year",
    subtitle: " Growth Rate",
    bg: "bg-[#4a1fb8]",
    icon: performanceIcon,
  },
];

// 2. Custom Counter Component for smooth animation
const AnimatedCounter = ({ value, suffix, isFloat }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime = null;
    const duration = 2000; // Animation duration in milliseconds (2 seconds)

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Ease-out function for a smooth slow-down effect at the end
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentVal = easeOut * value;

      if (isFloat) {
        setCount(currentVal.toFixed(2));
      } else {
        setCount(Math.floor(currentVal));
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Ensure it ends on the exact target value
        setCount(isFloat ? value : Math.floor(value));
      }
    };

    requestAnimationFrame(animate);
  }, [value, isFloat]);

  return (
    <span>
      {count}
      {suffix}
    </span>
  );
};

const DashboardCard = () => {
  return (
    <Card>
      <div className="text-center mb-8 mt-8 px-5 sm:px-8">
        <h2 className="text-[clamp(24px,3vw,28px)] font-bold leading-tight text-[#115E63]">
          Welcome to the NMDFC Digital Dashboard
        </h2>

        <p className="mt-6 font-semibold leading-relaxed text-slate-600">
          Gain a comprehensive view of NMDFC's progress and impact. Explore
          dashboards, statistics, reports, and insights that highlight the
          organization's initiatives, achievements, and contribution to
          empowering communities across the country.
        </p>
      </div>
      <Stats />
      <CTAButton type="button" onClick={() => requestExternalNavigation(POWER_BI_DASHBOARD_URL)}>
        View Dashboard →
      </CTAButton>
    </Card>
  );
};

const Stats = () => {
  const viewportRef = useMovingContentAccessibility();
  const [isPaused, setIsPaused] = useState(false);
  const loopedStats = [...stats, ...stats];

  return (
    <div className="w-full min-w-0 mt-8 relative">
      <div className="overflow-hidden" ref={viewportRef} onFocusCapture={() => setIsPaused(true)}>
      <StatsTrack style={{ animationPlayState: isPaused ? "paused" : undefined }}>
        {loopedStats.map((item, index) => (
          <div
            key={index}
            data-moving-item
            data-print-duplicate={index >= stats.length}
            className={`${item.bg} rounded-xl p-6 text-white shadow-md min-h-[140px] min-w-[min(220px,75vw)] relative flex-shrink-0`}
          >
            {/* 3. Replaced static amount with AnimatedCounter */}
            <p className="text-4xl font-bold">
              <AnimatedCounter 
                value={item.value} 
                suffix={item.suffix} 
                isFloat={item.isFloat} 
              />
            </p>

            <div className="mt-4">
              <p className="text-xl font-semibold">{item.title}</p>
              <p className="text-xl font-semibold">{item.subtitle}</p>

              <img
                src={item.icon}
                alt=""
                className="absolute right-4 bottom-4 opacity-20 w-20"
              />
            </div>
          </div>
        ))}
      </StatsTrack>
      </div>
      <div className="flex justify-end mt-2 pr-2">
        <PlaybackButton paused={isPaused} onClick={() => setIsPaused((prev) => !prev)} label="dashboard cards scrolling" />
      </div>
    </div>
  );
};

export default DashboardCard;
